/**
 * Error Codes
 *
 * Standardized error codes for the Bash system.
 * Use these codes for consistent error handling across packages.
 */

/**
 * Error code categories:
 * - SESSION_*: Session-related errors
 * - AGENT_*: Agent-related errors
 * - VALIDATION_*: Input validation errors
 * - SOCKET_*: Socket.io connection errors
 * - CONFIG_*: Configuration errors
 */
export const ErrorCode = {
  // Session errors
  SESSION_NOT_FOUND: 'SESSION_NOT_FOUND',
  SESSION_ALREADY_EXISTS: 'SESSION_ALREADY_EXISTS',
  SESSION_EXPIRED: 'SESSION_EXPIRED',
  SESSION_INVALID_STATE: 'SESSION_INVALID_STATE',

  // Agent errors
  AGENT_NOT_CONNECTED: 'AGENT_NOT_CONNECTED',
  AGENT_BUSY: 'AGENT_BUSY',
  AGENT_TIMEOUT: 'AGENT_TIMEOUT',
  AGENT_PROCESSING_ERROR: 'AGENT_PROCESSING_ERROR',
  AGENT_INTERRUPTED: 'AGENT_INTERRUPTED',

  // Validation errors
  VALIDATION_FAILED: 'VALIDATION_FAILED',
  INVALID_PAYLOAD: 'INVALID_PAYLOAD',
  INVALID_SESSION_ID: 'INVALID_SESSION_ID',
  INVALID_IMAGE_FORMAT: 'INVALID_IMAGE_FORMAT',

  // Socket errors
  SOCKET_NOT_CONNECTED: 'SOCKET_NOT_CONNECTED',
  SOCKET_CONNECTION_FAILED: 'SOCKET_CONNECTION_FAILED',
  SOCKET_DISCONNECTED: 'SOCKET_DISCONNECTED',

  // Configuration errors
  CONFIG_INVALID: 'CONFIG_INVALID',
  CONFIG_MISSING_REQUIRED: 'CONFIG_MISSING_REQUIRED',
  ENV_VALIDATION_FAILED: 'ENV_VALIDATION_FAILED',

  // General errors
  INTERNAL_ERROR: 'INTERNAL_ERROR',
  UNKNOWN_ERROR: 'UNKNOWN_ERROR',
} as const;

export type ErrorCodeType = (typeof ErrorCode)[keyof typeof ErrorCode];

/**
 * Human-readable error messages for each code
 */
export const ErrorMessages: Record<ErrorCodeType, string> = {
  // Session errors
  [ErrorCode.SESSION_NOT_FOUND]: 'Session not found',
  [ErrorCode.SESSION_ALREADY_EXISTS]: 'Session already exists',
  [ErrorCode.SESSION_EXPIRED]: 'Session has expired',
  [ErrorCode.SESSION_INVALID_STATE]: 'Session is in an invalid state for this operation',

  // Agent errors
  [ErrorCode.AGENT_NOT_CONNECTED]: 'Agent is not connected. Please wait for agent to connect.',
  [ErrorCode.AGENT_BUSY]: 'Agent is busy processing another message. Please wait.',
  [ErrorCode.AGENT_TIMEOUT]: 'Agent operation timed out',
  [ErrorCode.AGENT_PROCESSING_ERROR]: 'Agent encountered an error while processing',
  [ErrorCode.AGENT_INTERRUPTED]: 'Agent processing was interrupted',

  // Validation errors
  [ErrorCode.VALIDATION_FAILED]: 'Validation failed',
  [ErrorCode.INVALID_PAYLOAD]: 'Invalid payload format',
  [ErrorCode.INVALID_SESSION_ID]: 'Invalid session ID',
  [ErrorCode.INVALID_IMAGE_FORMAT]: 'Invalid image format',

  // Socket errors
  [ErrorCode.SOCKET_NOT_CONNECTED]: 'Socket is not connected',
  [ErrorCode.SOCKET_CONNECTION_FAILED]: 'Failed to establish socket connection',
  [ErrorCode.SOCKET_DISCONNECTED]: 'Socket disconnected unexpectedly',

  // Configuration errors
  [ErrorCode.CONFIG_INVALID]: 'Invalid configuration',
  [ErrorCode.CONFIG_MISSING_REQUIRED]: 'Missing required configuration',
  [ErrorCode.ENV_VALIDATION_FAILED]: 'Environment variable validation failed',

  // General errors
  [ErrorCode.INTERNAL_ERROR]: 'An internal error occurred',
  [ErrorCode.UNKNOWN_ERROR]: 'An unknown error occurred',
};
